import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Top from '../../components/UI/AppBars/Top';
import Bottom from '../../components/UI/AppBars/Bottom';
import NavMenu from '../../components/Navigation/NavMenu/NavMenu';
import { LayoutToaster } from '../../components/UI/Toaster/Toaster';
import OptionMenu from '../../components/UI/OptionMenu/OptionMenu';
import IdleTimer from '../IdleTimer/IdleTimer';
import IdleCheck from '../../components/General/IdleCheck';
import Lockscreen from '../../components/General/Lockscreen';
import { logoutUser } from '../../actions/authentication';
import { SCREEN_LOCK, BLUR_SCREEN } from '../../actions/types';
import Labels from '../../components/UI/Buttons/Dialogs/Labels';
import ErrorFAB from '../../components/UI/FAB/Errors';
import AppThemeProvider from '../../components/UI/Theme/AppThemeProvider';

/**
 * Full-page screens that must render WITHOUT app chrome even for a signed-in
 * user. The OAuth consent screen is one: it is a decision point handed to us by
 * an external client, and surrounding it with the nav menu invites the user to
 * wander off mid-authorization, leaving the waiting client hanging.
 */
const STANDALONE_PATHS = ['/oauth-authorize'];

const getMode = (auth) => {
    if (!auth.isAuthenticated) return 'login';
    if (auth.user?.kiosk) return 'kiosk';
    if (auth.user?.partner) return 'portal';
    return 'internal';
};

const Layout = ({ children }) => {
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const alert = useSelector(state => state.alert);
    const optionMenu = useSelector(state => state.optionMenu);
    const lock = useSelector(state => state.lock);
    const navMenu = useSelector(state => state.navMenu);
    const devices = useSelector(state => state.devices);

    const [idleWarning, setIdleWarning] = useState(false);
    const timerRef = useRef(null);
    const lockRef = useRef(lock);
    const idleWarningRef = useRef(idleWarning);
    lockRef.current = lock;
    idleWarningRef.current = idleWarning;

    const location = useLocation();
    const isStandalone = STANDALONE_PATHS.includes(location.pathname);

    const mode = getMode(auth);
    const isInternal = !isStandalone
        && mode === 'internal' && (auth.constants != null || auth.isInternalAdmin);
    const isReady = auth.isAuthenticated && auth.constants != null;

    const handleLogout = useCallback(() => {
        dispatch({ type: SCREEN_LOCK, payload: { screenLock: false } });
        dispatch({ type: BLUR_SCREEN, payload: { blurScreen: false } });
        setIdleWarning(false);
        dispatch(logoutUser(window.history));
    }, [dispatch]);

    // Idle timer — internal users only
    useEffect(() => {
        if (mode !== 'internal' || !auth.isAuthenticated) {
            if (timerRef.current) {
                timerRef.current.cleanUp();
                timerRef.current = null;
            }
            return;
        }

        timerRef.current = new IdleTimer({
            idleWarning: false,
            idleWarningTimeout: auth.user.sessionIdleWarningMinutes * 60000,
            idleTimeout: auth.user.sessionIdleMinutes * 60000,
            expiry: auth.user.sessionExpirationMinutes * 60000,
            screenLock: lockRef.current.screenLock,
            onIdleWarning: () => {
                if (!lockRef.current.screenLock && !idleWarningRef.current) {
                    dispatch({ type: BLUR_SCREEN, payload: { blurScreen: true } });
                    setIdleWarning(true);
                }
            },
            onIdleTimeout: () => {
                setIdleWarning(false);
                if (!lockRef.current.screenLock) {
                    dispatch({ type: SCREEN_LOCK, payload: { screenLock: true } });
                    dispatch({ type: BLUR_SCREEN, payload: { blurScreen: true } });
                }
            },
            onExpired: handleLogout
        });

        return () => {
            if (timerRef.current) {
                timerRef.current.cleanUp();
                timerRef.current = null;
            }
        };
    }, [mode, auth.isAuthenticated, dispatch, handleLogout]);

    const toggleNavDrawer = () => dispatch({ type: 'TOGGLE_NAV_DRAWER' });
    const toggleOptionMenu = () => dispatch({ type: 'TOGGLE_OPTIONMENU' });

    const toggleIdleCheck = () => {
        setIdleWarning(false);
        dispatch({ type: BLUR_SCREEN, payload: { blurScreen: false } });
    };

    const toggleLockscreen = () => {
        dispatch({ type: SCREEN_LOCK, payload: { screenLock: false } });
        dispatch({ type: BLUR_SCREEN, payload: { blurScreen: false } });
    };

    const DRAWERWIDTH = navMenu.drawerWidth;
    const drawerOpen = navMenu.drawerOpen;

    const contentSx = !isInternal
        ? { flex: '1 1 100%' }
        : {
            flex: '1 1 100%',
            maxWidth: '100%',
            paddingTop: '145px',
            marginRight: 1,
            marginTop: 0,
            marginLeft: drawerOpen
                ? { xs: 'auto', sm: 'auto', md: `${DRAWERWIDTH + 8}px`, lg: `${DRAWERWIDTH + 8}px` }
                : { xs: 'auto', sm: 'auto', md: 8, lg: 8 },
            marginBottom: drawerOpen
                ? { xs: 4, sm: 6, md: 6, lg: 6 }
                : { xs: 10, sm: 10, md: 10, lg: 8 }
        };

    return (
        <AppThemeProvider>
            <Box sx={{
                display: 'flow',
                minHeight: '100vh',
                maxWidth: '100vw',
                zIndex: 1,
                position: 'relative',
                marginLeft: 'auto',
                marginRight: 'auto',
                filter: lock.blurScreen ? 'blur(25px)' : null
            }}>
                <CssBaseline />

                {isInternal && (
                    <>
                        <Top drawerWidth={DRAWERWIDTH} open={drawerOpen} clicked={toggleNavDrawer} nodeEnv={auth.user.nodeEnv} />
                        <NavMenu drawerWidth={DRAWERWIDTH} open={drawerOpen} clicked={toggleNavDrawer} />
                        <OptionMenu drawerWidth={DRAWERWIDTH} open={optionMenu.open} clicked={toggleOptionMenu} onOpen={toggleOptionMenu} />
                    </>
                )}

                <Box sx={contentSx}>
                    {isReady && !isStandalone
                        ? <Container maxWidth={false}>{children}</Container>
                        : children}

                    {isReady && idleWarning && (
                        <IdleCheck
                            open={idleWarning}
                            toggleDialog={toggleIdleCheck}
                            cancel={handleLogout}
                            timeLeft={auth.user.sessionIdleWarningMinutes}
                        />
                    )}

                    {isReady && lock.screenLock && (
                        <Lockscreen open={lock.screenLock} toggleDialog={toggleLockscreen} />
                    )}
                </Box>

                {isReady && devices.openPrintDialog && <Labels />}
                {isReady && !isStandalone && <ErrorFAB />}
                <LayoutToaster alert={alert} />
                {isInternal && <Bottom open={drawerOpen} clicked={toggleNavDrawer} />}
            </Box>
        </AppThemeProvider>
    );
};

export default Layout;
